import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Star } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../api/client";

import "./HeroBanner.css";
import logger from "../../utils/logger.js";

const HeroBanner = ({ products = [] }) => {
  const { lang } = useLanguage();
  const { isLogged } = useAuth();
  const navigate = useNavigate();

  const isAr = lang === "ar";

  const [activeSlide, setActiveSlide] = useState(0);

  //  السلايدز من المنتجات الحقيقية
  const slides = products;

  useEffect(() => {
    if (slides.length === 0) return;

    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [slides.length]);

  if (!slides.length) return null;

  const currentSlide = slides[activeSlide];

  const productName = isAr
    ? currentSlide.name_ar || currentSlide.name_en
    : currentSlide.name_en || currentSlide.name_ar;

  const originalPriceCandidate = Number(
    currentSlide.original_price ?? currentSlide.old_price ?? currentSlide.price ?? 0
  );
  const currentPriceCandidate = Number(
    currentSlide.final_price ?? currentSlide.sale_price ?? currentSlide.price ?? 0
  );
  const hasDiscount =
    currentSlide.has_discount === true ||
    (originalPriceCandidate > 0 &&
      currentPriceCandidate > 0 &&
      currentPriceCandidate < originalPriceCandidate);
  const price = currentPriceCandidate > 0 ? currentPriceCandidate : 0;
  const originalPrice = hasDiscount ? originalPriceCandidate : null;

  const discountPercent =
    originalPrice && price
      ? Math.round(((originalPrice - price) / originalPrice) * 100)
      : 0;

  //  جلب variant من المنتج
  const variantId = currentSlide?.variants?.[0]?.id;

  async function handleShopNow() {
    if (!isLogged) {
      navigate("/login");
      return;
    }

    if (!variantId) {
      logger.error("No variant found in product", currentSlide);
      return;
    }

    try {
      await api.apiPost("cart/add", {
        variant_id: variantId,
        quantity: 1,
      });

      navigate("/cart");
    } catch (err) {
      logger.error(err);
      navigate("/cart");
    }
  }

  //  صورة المنتج (نفس منطق ProductDetail)
  const imageSrc =
    currentSlide?.image_url ||
    (currentSlide?.image && /^https?:\/\//i.test(currentSlide.image)
      ? currentSlide.image
      : null);

  return (
    <section className="heroSection" dir={isAr ? "rtl" : "ltr"}>
      <div className="heroBanner">
        <div className="bannerContent">
          <div className="bannerText">
            {/* badge */}
            <div className="brandBadge">
              <span className="badgeIcon">
                <Star size={16} />
              </span>
              <p className="badgeText">
                {isAr ? "عروض حصرية" : "Exclusive Offers"}
              </p>
            </div>

            {/* name */}
            <h1 className="mainTitle">{productName}</h1>

            {/* discount */}
            {discountPercent > 0 && (
              <div className="discountBadge">
                <span className="discountText">
                  {isAr ? "خصم يصل إلى" : "Discount up to"}
                </span>
                <span className="discountPercent">
                  {discountPercent}%
                </span>
              </div>
            )}

            {/* price */}
            <div className="priceContainer">
              <span className="salePrice">${price}</span>

              {originalPrice && (
                <span className="originalPrice">
                  ${originalPrice}
                </span>
              )}
            </div>

            {/*  زر Shop Now */}
            <button className="shopNowLink" onClick={handleShopNow}>
              {isAr ? "تسوق الآن" : "Shop Now"}
              {isAr ? (
                <ArrowLeft className="arrowIcon" size={18} />
              ) : (
                <ArrowRight className="arrowIcon" size={18} />
              )}
            </button>
          </div>

          {/* image */}
          <div className="bannerImage">
            <div className="imageGlow"></div>

            {imageSrc ? (
              <img
                src={imageSrc}
                alt={productName}
                className="productImage"
              />
            ) : (
              <div className="noImage">
                {isAr ? "لا توجد صورة" : "No image"}
              </div>
            )}
          </div>
        </div>

        {/* dots */}
        <div className="bannerDots">
          {slides.map((_, i) => (
            <span
              key={i}
              className={`dot ${activeSlide === i ? "active" : ""}`}
              onClick={() => setActiveSlide(i)}
            ></span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
